import React, { useEffect, useState } from 'react';
import api from '../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../styles/floatingForm.css';

const UpdateMainPageInPage = () => {

  const navigate = useNavigate();

  // Get ID from URL
  const { id } = useParams();

  const [subpages, setSubpages] = useState([]);

  const [selectedSubpage, setSelectedSubpage] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const [image1, setImage1] = useState(null);
  const [image2, setImage2] = useState(null);

  const [existingImage1, setExistingImage1] = useState('');
  const [existingImage2, setExistingImage2] = useState('');

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);


  // --------------------------------------------------
  // FETCH MAIN PAGE
  // --------------------------------------------------

  const fetchMainpage = async () => {

    if (!id) return;

    try {

      const response = await api.get(
        `/pagedetail/get/${id}`
      );

      const mainpage =
        response.data?.data;

      if (mainpage) {

        setTitle(
          mainpage.title || ''
        );

        setDescription(
          mainpage.description || ''
        );

        setSelectedSubpage(
          mainpage.subpage_id || ''
        );

        setExistingImage1(
          mainpage.image1 || ''
        );

        setExistingImage2(
          mainpage.image2 || ''
        );

      } else {

        toast.error(
          'Main page data not found'
        );

      }

    } catch (error) {

      console.error(
        'Error fetching mainpage:',
        error.response?.data ||
        error.message
      );

      toast.error(
        'Failed to fetch mainpage data'
      );

    } finally {

      setLoading(false);

    }

  };


  // --------------------------------------------------
  // FETCH SUBPAGES
  // --------------------------------------------------

  const fetchSubpages = async () => {

    try {

      const response = await api.get(
        '/subpageapi/get-all'
      );

      setSubpages(
        response.data.data || []
      );

    } catch (error) {

      console.error(
        'Error fetching subpages:',
        error
      );

      toast.error(
        'Failed to fetch subpages'
      );

    }

  };


  // --------------------------------------------------
  // FETCH DATA
  // --------------------------------------------------

  useEffect(() => {

    fetchMainpage();
    fetchSubpages();

  }, [id]);


  // --------------------------------------------------
  // IMAGE VALIDATION
  // --------------------------------------------------

  const validateImageDimensions = (
    file,
    maxWidth,
    maxHeight
  ) => {

    return new Promise(
      (resolve, reject) => {

        const image = new Image();

        image.onload = () => {

          if (
            image.width <= maxWidth &&
            image.height <= maxHeight
          ) {

            resolve(true);

          } else {

            resolve(false);

          }

        };

        image.onerror = () => {

          reject(
            new Error(
              'Invalid image file'
            )
          );

        };

        image.src =
          URL.createObjectURL(file);

      }
    );

  };


  // --------------------------------------------------
  // SUBMIT
  // --------------------------------------------------

  const handleSubmit = async (e) => {

    e.preventDefault();

    let validationErrors = {};


    // SUBPAGE

    if (!selectedSubpage) {

      validationErrors.selectedSubpage =
        'select subpage';

    }


    // TITLE

    if (!title.trim()) {

      validationErrors.title =
        'title cannot be empty';

    }


    // DESCRIPTION

    if (!description.trim()) {

      validationErrors.description =
        'description cannot be empty';

    }


    const allowedImageTypes = [
      'image/png',
      'image/jpeg'
    ];


    // --------------------------------------------------
    // IMAGE 1
    // --------------------------------------------------

    if (image1) {

      if (
        !allowedImageTypes.includes(
          image1.type
        )
      ) {

        validationErrors.image1 =
          'Only PNG and JPG files are allowed';

      } else {

        const isValidImage =
          await validateImageDimensions(
            image1,
            1200,
            630
          );

        if (!isValidImage) {

          validationErrors.image1 =
            'Image1 must be under 1200px by 630px';

        }

      }

    }


    // --------------------------------------------------
    // IMAGE 2
    // --------------------------------------------------

    if (image2) {

      if (
        !allowedImageTypes.includes(
          image2.type
        )
      ) {

        validationErrors.image2 =
          'Only PNG and JPG files are allowed';

      } else {

        const isValidImage =
          await validateImageDimensions(
            image2,
            1200,
            630
          );

        if (!isValidImage) {

          validationErrors.image2 =
            'Image2 must be under 1200px by 630px';

        }

      }

    }


    // --------------------------------------------------
    // VALIDATION
    // --------------------------------------------------

    if (
      Object.keys(validationErrors).length > 0
    ) {

      setErrors(validationErrors);

      return;

    }


    setErrors({});


    // --------------------------------------------------
    // FORM DATA
    // --------------------------------------------------

    const formData = new FormData();

    formData.append(
      'title',
      title
    );

    formData.append(
      'description',
      description
    );

    formData.append(
      'subpage_id',
      selectedSubpage
    );


    if (image1) {

      formData.append(
        'image1',
        image1
      );

    }


    if (image2) {

      formData.append(
        'image2',
        image2
      );

    }


    // --------------------------------------------------
    // UPDATE API
    // --------------------------------------------------

    try {

      const response = await api.post(
        `/pagedetail/update/${id}`,
        formData,
        {
          headers: {
            'Content-Type':
              'multipart/form-data'
          }
        }
      );


      if (
        response.data.status === "success"
      ) {

        toast.success(
          `Mainpage updated successfully! ID: ${id}`
        );

        // Go back to list
        navigate('/display-pages');

      } else {

        toast.error(
          'Failed to update mainpage'
        );

      }

    } catch (error) {

      console.error(
        'Error updating mainpage:',
        error
      );

      toast.error(
        'Error updating mainpage'
      );

    }

  };


  // --------------------------------------------------
  // CANCEL
  // --------------------------------------------------

  const handleCancel = () => {

    navigate('/display-pages');

  };


  return (
    <div className='floating-form-container' >
      <div className="heading">
        <h2>Update Mainpage List</h2>
      </div>

        <form onSubmit={handleSubmit}>
            <div className="form-container">

                <div className="section-1">
                    <div className="input-container-floating-form wid-50">
                        <label>Select Subpage *</label>
                        <select className={`form-control ${errors.selectedSubpage ? 'is-invalid' : ''}`} value={selectedSubpage} onChange={(e) => setSelectedSubpage(e.target.value)}  >
                  <option value="">Select Subpage</option>
                  {subpages.map((subpage) => (
                    <option key={subpage.id} value={subpage.id}>
                      {subpage.subpage}
                    </option>
                  ))}
                </select>
                <b>{errors.selectedSubpage && <span className='text-danger small'>{errors.selectedSubpage}</span>}</b>
                    </div>

                    <div className="input-container-floating-form wid-50">
                        <label >Title *</label>
                        <input type="text" className={`form-control ${errors.title ? 'is-invalid' : ''}`} value={title} onChange={(e) => setTitle(e.target.value)}  />
                <b>{errors.title && <span className='text-danger small'>{errors.title}</span>}</b>
                    </div>

                    

                    <div className="input-container-floating-form wid-100">
                        <label htmlFor="aboutUs">Page Description *</label>
                        
                        <ReactQuill value={description} onChange={setDescription} className={`react-quill `} placeholder="Enter Description" />
                        
                    </div>
                </div>

                <hr className='form-rule' />

                <div className="section-1">

                    <h2 className='wid-100'>Upload Image</h2>

                    <div className="input-container-floating-form wid-50">
                        <label htmlFor="logo">Upload Image 1 * <br /> (only png and Jpg size 1200px*630px)</label>
                        
                        <div class="file-select">
                            <div class="file-select-button" id="fileName">Choose File</div>
                            <div class="file-select-name" id="noFile">{image1?.name || "No file chosen..."}</div> 
                            <input type="file" id="logo" onChange={(e) => setImage1(e.target.files[0])} />
                        </div>
                            <b>{errors.logoImageFile && <span className="text-danger small">{errors.logoImageFile}</span>}</b>
                            <div className='mt-2'>
                        {existingImage1 && !image1 && (
                    <img
                      style={{ width: '100px', height: '100px' }}
                      src={`${process.env.REACT_APP_IMAGE_BASE_URL}${existingImage1}`}
                      alt="Image 1"
                    />
                  )}
                      </div>

                    </div>
                    <div className="input-container-floating-form wid-50">
                        <label htmlFor="banner">Upload Image 2 <br /> (only png and Jpg size 1200px*630px)</label>
                        
                        <div class="file-select">
                            <div class="file-select-button" >Choose File</div>
                            <div class="file-select-name" >{image2?.name || "No file chosen..."}</div> 
                            <input type="file" id="banner" onChange={(e) => setImage2(e.target.files[0])} />
                        </div>
                            <b>{errors.bannerFile && <span className='text-danger small'>{errors.bannerFile}</span>}</b>
                            {existingImage2 && !image2 && (
                    <img
                      style={{ width: '100px', height: '100px' }}
                      src={`${process.env.REACT_APP_IMAGE_BASE_URL}${existingImage2}`}
                      alt="Image 2"
                    />
                  )}

                    </div>
                </div>
                <div className="form-btn-container">

                    <button type='submit'>Update</button>
                    <button type='reset' onClick={handleCancel}>Cancel</button>

                </div>
            </div>

       


        </form>

    </div>
  );
};

export default UpdateMainPageInPage;
